import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectIsAcceptModalOpen, selectAcceptModalContext } from '../../store/selectors';
import { closeAcceptModal } from '../../store/slice';
import { Modal, Box, Typography, IconButton, Button } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { getAcceptModal } from '../../registry/actionTypeRegistry';
import { SentryErrorBoundary } from '../../../../services/SentryService';
import { useTracking } from '../../../../context/TrackingContext';
import { EventType, InteractionType } from '../../../../types/events';
import { useCardManagement } from '../../contexts/CardManagementContext';

/**
 * Central accept modal component that renders different content
 * based on the action type using the registry pattern
 */
export const AcceptModal: React.FC = () => {
  const dispatch = useDispatch();
  const isOpen = useSelector(selectIsAcceptModalOpen);
  const context = useSelector(selectAcceptModalContext);
  const [error, setError] = useState<string | null>(null);

  // Analytics tracking
  const { trackEvent } = useTracking();

  // Card management functions
  const { resetCard, animateAccept } = useCardManagement();

  const handleClose = () => {
    // Reset the card to its original state when modal is canceled
    if (context) {
      resetCard(context.action.action_id);
    }

    setError(null);
    dispatch(closeAcceptModal());
  };

  const handleAccept = async (actionId: string, updatedPayload?: any) => {
    if (!context) return;

    try {
      setError(null);

      // Trigger the card animation which will handle the API call
      animateAccept(actionId, updatedPayload);

      // Close modal immediately - the animation will handle the API call
      dispatch(closeAcceptModal());
    } catch (error) {
      console.error('❌ Error accepting action:', error);
      setError('Failed to accept the suggestion. Please try again.');
    }
  };

  if (!isOpen || !context) {
    return null;
  }

  // Get the appropriate content component for this action type
  const ContentComponent = getAcceptModal(context.actionType);

  // If no accept modal is defined for this action type, don't render anything
  if (!ContentComponent) {
    console.warn(`No accept modal defined for action type: ${context.actionType}`);
    return null;
  }

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="accept-modal-title"
      aria-describedby="accept-modal-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 550,
          maxWidth: '90vw',
          maxHeight: '90dvh',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 0,
          outline: 'none',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Modal Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 3,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography id="accept-modal-title" variant="h6" component="h2">
            Review Before Accepting
          </Typography>
          <IconButton onClick={handleClose} size="small" sx={{ ml: 1 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Modal Content */}
        <Box sx={{ p: 3, flexGrow: 1, overflowY: 'auto' }}>
          {error && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                bgcolor: 'error.light',
                borderRadius: 1,
                color: 'error.contrastText',
              }}
            >
              <Typography variant="body2">{error}</Typography>
            </Box>
          )}

          <SentryErrorBoundary
            fallback={({ error, resetError }) => (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="subtitle1" color="error.main" sx={{ mb: 2 }}>
                  Unable to load accept options
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Something went wrong. Please try again.
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  <Button onClick={resetError} variant="outlined" color="primary">
                    Try Again
                  </Button>
                  <Button onClick={handleClose} variant="outlined" color="inherit">
                    Close
                  </Button>
                </Box>
              </Box>
            )}
            beforeCapture={scope => {
              scope.setTag('component', 'AcceptModalContent');
              scope.setContext('modal', {
                actionType: context.actionType,
                actionId: context.action.action_id,
              });
            }}
          >
            <ContentComponent context={context} onAccept={handleAccept} onCancel={handleClose} />
          </SentryErrorBoundary>
        </Box>
      </Box>
    </Modal>
  );
};
